import CloseIcon from '@mui/icons-material/Close'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt'
import { alpha, Box, Button, Divider, Stack, Tooltip, Typography, useTheme } from '@mui/material'
import SharedSelect from 'components/shared/Select/SharedSelect'
import React from 'react'
import VersionChip from 'theme/components/Chip/VersionChip'
import { ProductSortMode } from 'types'
import ContractActionsMenu from './ContractActionsMenu'

type ContractToolbarProps = {
  // Sort props
  sortMode: ProductSortMode
  onSortChange: (mode: ProductSortMode) => void
  // Status props
  isEditing: boolean
  isDirty: boolean
  productsDirty: boolean
  isSaveRequired: boolean
  // Action props
  ok: boolean
  deal: any
  productRows: any[]
  productChanges: any
  activeUser: any
  onSaveAll: () => void
  onSaveAndUpdate: (e: React.MouseEvent<HTMLButtonElement>) => void
  onCancel: () => void
  onEdit: () => void
  onRequest: () => void
  onCreate: (e: React.MouseEvent<HTMLButtonElement>) => void
  isRequesting: boolean
  isCreating: boolean
  validationErrors: any
  resetDealBaseline: () => void
  /** When true, renders without its own container (embedded in a parent bar) */
  embedded?: boolean
  /** When true, minimizes buttons to icon-only (e.g. when AI agent panel is docked) */
  compact?: boolean
}

const ContractToolbar: React.FC<ContractToolbarProps> = ({
  sortMode,
  onSortChange,
  isEditing,
  isDirty,
  productsDirty,
  isSaveRequired,
  ok,
  deal,
  productRows,
  productChanges,
  activeUser,
  onSaveAll,
  onSaveAndUpdate,
  onCancel,
  onEdit,
  onRequest,
  onCreate,
  isRequesting,
  isCreating,
  validationErrors,
  resetDealBaseline,
  embedded = false,
  compact = false,
}) => {
  const theme = useTheme()
  const primaryAlpha = (opacity: number) => alpha(theme.palette.primary.main, opacity)
  const errorAlpha = (opacity: number) => alpha(theme.palette.error.main, opacity)

  const sortOptions = [
    { value: 'NAME_CATEGORY_ASC', label: 'Name, then Category' },
    { value: 'NAME_ASC', label: 'Name (A-Z)' },
    { value: 'NAME_DESC', label: 'Name (Z-A)' },
    { value: 'DATE_ASC', label: 'Date (Earliest First)' },
    { value: 'DATE_DESC', label: 'Date (Latest First)' },
    { value: 'CATEGORY_ASC', label: 'Category (A-Z)' },
    { value: 'CATEGORY_DESC', label: 'Category (Z-A)' },
    { value: 'SUBCATEGORY_ASC', label: 'Subcategory (A-Z)' },
    { value: 'SUBCATEGORY_DESC', label: 'Subcategory (Z-A)' },
  ]

  const buttonSx = {
    textTransform: 'none' as const,
    fontSize: 12,
    fontWeight: 600,
    borderRadius: 2,
    px: 1.5,
    py: 0.5,
    minHeight: 34,
    lineHeight: 1.2,
  }

  return (
    <Box
      sx={{
        ...(embedded
          ? { py: 0.5 }
          : {
              bgcolor: alpha(theme.palette.text.primary, 0.02),
              borderBottom: '1px solid',
              borderColor: 'divider',
              borderRadius: '12px 12px 0 0',
              px: 2,
              py: 1,
            }),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
      }}
    >
      {/* Left: Version + Sort */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {deal?.semanticVersion && <VersionChip version={deal?.semanticVersion} />}
        <SharedSelect
          id={'toolbar-sort-label'}
          label="Sort By"
          value={sortMode}
          options={sortOptions}
          onChange={e => onSortChange(e.target.value as ProductSortMode)}
        />
      </Box>

      {/* Right: Action Buttons */}
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ rowGap: 0.5 }}>
        {isEditing ? (
          <>
            <Button
              variant="outlined"
              size="small"
              startIcon={compact ? undefined : <CloseIcon sx={{ fontSize: '18px !important' }} />}
              onClick={onCancel}
              sx={{
                ...buttonSx,
                ...(compact && { minWidth: 34, px: 0.75 }),
                color: 'text.secondary',
                borderColor: alpha(theme.palette.text.primary, 0.15),
                '&:hover': {
                  borderColor: 'error.main',
                  color: 'error.main',
                  bgcolor: errorAlpha(0.06),
                },
              }}
            >
              {compact ? <CloseIcon sx={{ fontSize: 16 }} /> : 'Cancel'}
            </Button>
            {deal?.contractId ? (
              <Tooltip
                title={
                  <Typography sx={{ fontSize: 13 }}>
                    {!isSaveRequired ? 'No changes to save' : 'Save & Update Contract'}
                  </Typography>
                }
              >
                <span>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={compact ? undefined : <SystemUpdateAltIcon sx={{ fontSize: '18px !important' }} />}
                    onClick={onSaveAndUpdate}
                    disabled={!isSaveRequired}
                    sx={{
                      ...buttonSx,
                      ...(compact && { minWidth: 34, px: 0.75 }),
                      bgcolor: isSaveRequired ? 'primary.main' : undefined,
                      ...(isSaveRequired && {
                        animation: 'pulse 2s ease-in-out infinite',
                        '@keyframes pulse': {
                          '0%, 100%': { boxShadow: '0 0 0 0 rgba(52,152,219,0.4)' },
                          '50%': { boxShadow: '0 0 0 6px rgba(52,152,219,0)' },
                        },
                      }),
                    }}
                  >
                    {compact ? <SystemUpdateAltIcon sx={{ fontSize: 16 }} /> : 'Save & Update'}
                  </Button>
                </span>
              </Tooltip>
            ) : (
              <Tooltip
                title={
                  <Typography sx={{ fontSize: 13 }}>
                    {!isSaveRequired ? 'No changes to save' : 'Save Agreement'}
                  </Typography>
                }
              >
                <span>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={compact ? undefined : <SaveIcon sx={{ fontSize: '18px !important' }} />}
                    onClick={onSaveAll}
                    disabled={!isSaveRequired}
                    sx={{
                      ...buttonSx,
                      ...(compact && { minWidth: 34, px: 0.75 }),
                      bgcolor: isSaveRequired ? 'primary.main' : undefined,
                      ...(isSaveRequired && {
                        animation: 'pulse 2s ease-in-out infinite',
                        '@keyframes pulse': {
                          '0%, 100%': { boxShadow: '0 0 0 0 rgba(52,152,219,0.4)' },
                          '50%': { boxShadow: '0 0 0 6px rgba(52,152,219,0)' },
                        },
                      }),
                    }}
                  >
                    {compact ? <SaveIcon sx={{ fontSize: 16 }} /> : 'Save'}
                  </Button>
                </span>
              </Tooltip>
            )}
          </>
        ) : (
          <>
            <Tooltip
              title={
                <Typography sx={{ fontSize: 13 }}>
                  {!deal?.isDraft ? 'Cannot edit (locked)' : 'Edit Agreement'}
                </Typography>
              }
            >
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={compact ? undefined : <EditIcon sx={{ fontSize: '18px !important' }} />}
                  onClick={onEdit}
                  disabled={!deal?.isDraft}
                  sx={{
                    ...buttonSx,
                    ...(compact && { minWidth: 34, px: 0.75 }),
                    borderColor: alpha(theme.palette.text.primary, 0.15),
                    '&:hover': {
                      borderColor: 'primary.main',
                      color: 'primary.main',
                      bgcolor: primaryAlpha(0.06),
                    },
                  }}
                >
                  {compact ? <EditIcon sx={{ fontSize: 16 }} /> : 'Edit'}
                </Button>
              </span>
            </Tooltip>
            {isSaveRequired && (
              <>
                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                {deal?.contractId ? (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={compact ? undefined : <SystemUpdateAltIcon sx={{ fontSize: '18px !important' }} />}
                    onClick={onSaveAndUpdate}
                    sx={{
                      ...buttonSx,
                      ...(compact && { minWidth: 34, px: 0.75 }),
                      animation: 'pulse 2s ease-in-out infinite',
                      '@keyframes pulse': {
                        '0%, 100%': { boxShadow: `0 0 0 0 ${primaryAlpha(0.4)}` },
                        '50%': { boxShadow: `0 0 0 6px ${primaryAlpha(0)}` },
                      },
                    }}
                  >
                    {compact ? <SystemUpdateAltIcon sx={{ fontSize: 16 }} /> : 'Save & Update'}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={compact ? undefined : <SaveIcon sx={{ fontSize: '18px !important' }} />}
                    onClick={onSaveAll}
                    sx={{
                      ...buttonSx,
                      ...(compact && { minWidth: 34, px: 0.75 }),
                      animation: 'pulse 2s ease-in-out infinite',
                      '@keyframes pulse': {
                        '0%, 100%': { boxShadow: `0 0 0 0 ${primaryAlpha(0.4)}` },
                        '50%': { boxShadow: `0 0 0 6px ${primaryAlpha(0)}` },
                      },
                    }}
                  >
                    {compact ? <SaveIcon sx={{ fontSize: 16 }} /> : 'Save'}
                  </Button>
                )}
              </>
            )}
          </>
        )}

        {!isEditing && (
          <>
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
            <ContractActionsMenu
              deal={deal}
              productRows={productRows}
              productChanges={productChanges}
              activeUser={activeUser}
              isRequesting={isRequesting}
              onRequestApproval={onRequest}
              onCreate={onCreate}
              validationErrors={validationErrors}
              isCreating={isCreating}
              isValid={ok ?? false}
              isDisabled={!ok}
              resetDealBaseline={resetDealBaseline}
              isSaveRequired={isSaveRequired}
              direction="row"
              spacing={1}
              sizeConfig={{
                buttonSize: 40,
                iconSize: 32,
                muiIconSize: 32,
                badgeSize: 18,
              }}
              labeledMode
              compact={compact}
            />
          </>
        )}
      </Stack>
    </Box>
  )
}

export default ContractToolbar
